<template>
  <SectionContainer size="md">
    <HeaderBenefits />
    <HomepageBanner />
  </SectionContainer>

  <SectionContainer bg-color="light" height="sm">
    <HomepageChatSection />
  </SectionContainer>

  <ProductOverviewSection
    v-if="state.status === 'success' && state.data?.popularProducts?.length"
    :sections="state.data?.popularProducts ?? []"
    show-all-button
    title-color="primary"
  />

  <SectionContainer height="md">
    <HomepageExplanation />
    <lazyExplanationAnimation />
    <HomepageProductSearch />
  </SectionContainer>

  <SectionContainer v-if="locale !== 'fr-FR'" height="md">
    <HomepageReviews />
  </SectionContainer>

  <HomepageStatistics />

  <SectionContainer v-if="state.status === 'error'">
    <SErrorMessage retry size="lg" @retry="refetch" />
  </SectionContainer>
  <SectionContainer
    v-else-if="
      state.status === 'success' &&
      state.data?.mostRequested &&
      mostRequested.length
    "
    height="md"
  >
    <HomepagePopularProducts
      :products="mostRequested"
      :title="state.data?.mostRequested?.title ?? ''"
    />
  </SectionContainer>

  <HomepageCompanyRegistration />
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { defineLazyHydrationComponent } from "#imports";
import { computed } from "vue";

import { SErrorMessage } from "@solvari/components";

import { useHomepagePage } from "~/features/homepage/api/cms.api.ts";
import HomepageBanner from "~/features/homepage/components/HomepageBanner.vue";
import HomepageChatSection from "~/features/homepage/components/HomepageChatSection.vue";
import HomepageCompanyRegistration from "~/features/homepage/components/HomepageCompanyRegistration.vue";
import HomepageExplanation from "~/features/homepage/components/HomepageExplanation.vue";
import HomepagePopularProducts from "~/features/homepage/components/HomepagePopularProducts.vue";
import HomepageProductSearch from "~/features/homepage/components/HomepageProductSearch.vue";
import HomepageReviews from "~/features/homepage/components/HomepageReviews.vue";
import HomepageStatistics from "~/features/homepage/components/HomepageStatistics.vue";
import ProductOverviewSection from "~/features/product-overview/components/ProductOverviewSection.vue";
import HeaderBenefits from "~/shared/components/header/HeaderBenefits.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { keepPageWarm } from "~/shared/composables/useCacheTags.ts";
import { useCmsSeo } from "~/shared/composables/useCmsSeo.ts";
import { useHostLocale } from "~/shared/composables/useHost.ts";

// Below the fold, animejs + 124 kB of animation data.
const lazyExplanationAnimation = defineLazyHydrationComponent(
  "visible",
  () => import("~/shared/libs/lazy/explanationAnimation.ts"),
);

const locale = useHostLocale();

const { state, refetch } = useQuery(useHomepagePage({ locale }));

keepPageWarm();

const mostRequested = computed(() =>
  (state.value.data?.mostRequested?.items ?? [])
    .filter((item) => item.page)
    .map((item) => ({
      description: item.page.seo?.description ?? null,
      href: item.page.url ?? "",
      id: item.id,
      image: item.page.seo?.image?.url ?? null,
      name: item.page.heroTitle ?? item.page.title,
    })),
);

useCmsSeo(() => state.value.data?.seo, {
  variantSourceUrl: () => state.value.data?.variantSource?.url,
});
</script>
